import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';

import { UserProfile, isUserProfile, BaseUserProfile } from './userProfile';
import Identity from './identity';

import { headers, logPrefix } from './constants';

/**
 * Updates the logged in user's profile
 *
 * @param profile
 *
 * @returns
 * A __Promise__ which resolves to the user's updated details. The `userProfile` property will be updated with this data.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *   "firstName": "John",
 *   "lastName": "Doe",
 *   "secondLastName": "Doey",
 *   "displayName": "john_doe",
 *   "gender": "MALE, FEMALE",
 *   "email": "john.doe@donotreply.com",
 *   "picture": "KEY1",
 *   "birthYear": "1999",
 *   "birthMonth": "11",
 *   "birthDay": "15",
 *   "emailVerified": true,
 *   "contacts": [
 *     {
 *       "phone": "555-555-5555",
 *       "type": "WORK, HOME, PRIMARY, OTHER"
 *     }
 *   ],
 *   "addresses": [
 *     {
 *       "line1": "123 Main St.",
 *       "line2": "string",
 *       "locality": "string",
 *       "region": "string",
 *       "postal": "string",
 *       "country": "string",
 *       "type": "WORK, HOME, PRIMARY, OTHER"
 *     }
 *   ],
 *   "attributes": [
 *     {
 *       "name": "KEY1",
 *       "value": "VALUE 1",
 *       "type": "String, Number, Date, Boolean"
 *     }
 *   ],
 *   "identities": [
 *     {
 *       "createdOn": "2019-05-30T12:35:50.041Z",
 *       "createdBy": "string",
 *       "modifiedOn": "2019-05-30T12:35:50.042Z",
 *       "modifiedBy": "string",
 *       "deletedOn": "2019-05-30T12:35:50.042Z",
 *       "id": 1,
 *       "userName": "jdoe123",
 *       "passwordReset": true,
 *       "type": "Identity",
 *       "lastLoginDate": "2019-05-30T12:35:50.042Z",
 *       "locked": true
 *     }
 *   ],
 *   "legacyId": "FGE234UIR184",
 *   "uuid": "string"
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/updateProfile
 * ```
 * </details>
 */

export default async function updateUserProfile(
  profile: BaseUserProfile
): Promise<UserProfile | APIErrorResponse> {
  const isLoggedIn = await Identity.isLoggedIn();
  if (!isLoggedIn) {
    throw new Error(`${logPrefix} Missing or invalid jwt`);
  }

  // remove uuid if accidentally passed in as the profile update data.
  if (isUserProfile(profile)) {
    delete profile.uuid;
  }

  return fetch(`${Identity.apiOrigin}/identity/public/v1/profile`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
      ...headers
    },
    body: JSON.stringify(profile)
  })
    .then(JSONResponseHandler)
    .then(json => {
      if (isUserProfile(json)) {
        Identity.userProfile = json;
        return Identity.userProfile;
      } else {
        throw json;
      }
    });
}
